// ============================================
// estiloExpedientes.js — estilo e helpers COMPARTIDOS de la torre de control
// ============================================
// Fuente unica de identidad Beep + formateadores para las vistas de expedientes
// de direccion (ControlExpedientes, ModalAvisarResponsable),
// para no duplicar constantes ni caer en imports circulares entre componentes.

import React from 'react';

export const FF = "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export const C = {
  navy: '#2f5f9e', azul: '#5c9adb', lima: '#dcde74', gris: '#4e5357',
  grisClaro: '#8a9099', fondo: '#f3f4f4', borde: '#e6e8eb',
  blanco: '#ffffff', oscuro: '#1f2937',
};

export const SEMAFORO = {
  verde:    { color: '#16a34a', bg: '#f0fdf4', borde: '#bbf7d0', label: 'Al corriente' },
  amarillo: { color: '#d97706', bg: '#fffbeb', borde: '#fde68a', label: 'Tardando' },
  rojo:     { color: '#dc2626', bg: '#fef2f2', borde: '#fecaca', label: 'Atorado' },
};

export const TIPO_VISUAL = {
  instalacion:  { label: 'Instalacion',  color: '#2f5f9e', bg: '#eef4fb' },
  // RENOMBRE-SERVICIOS (2026-07-09): codigo interno 'reparaciones', se LEE "Servicios".
  reparaciones: { label: 'Servicios', color: '#9b59b6', bg: '#f5eefa' },
  ventas:       { label: 'Ventas',       color: '#16a34a', bg: '#eefaf1' },
};

// Formato de moneda; maneja null con "—", sin inventar datos. En modo compacto
// abrevia a "k"/"M" para no romper tarjetas angostas.
export function formatMoneda(n, compacto) {
  if (n == null || isNaN(n)) return '—';
  if (compacto && n >= 1000) {
    const miles = n / 1000;
    // SIN-REDONDEO: OK (resumen abreviado "$12k" de un tablero, no es un importe)
    return '$' + (miles >= 1000 ? (miles / 1000).toFixed(1) + 'M' : Math.round(miles) + 'k');
  }
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export function formatFechaCorta(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

export function textoDias(d) {
  const n = Number(d) || 0;
  if (n <= 0) return 'Hoy';
  if (n === 1) return '1 dia';
  return `${n} dias`;
}

// Marca discreta cuando el monto proviene de una cotizacion AUN NO autorizada
// (para que direccion no lo lea como cifra confirmada).
export function MarcaSinAutorizar({ exp }) {
  if (exp.monto_oculto || exp.monto == null || exp.monto_autorizado) return null;
  return (
    <span title="Cotizacion aun no autorizada" style={{ color: '#c2c7ce', marginLeft: 2, fontWeight: 700 }}>*</span>
  );
}

// ============================================
// MONTO DEL EXPEDIENTE — regla de Ricardo (MOVIL-2, 2026-09-14)
// ============================================
// El dinero NO se recalcula aqui: `monto` y `monto_autorizado` llegan resueltos del backend
// (COALESCE cotizacion autorizada -> ultima capturada, la MISMA formula que /api/control-expedientes).
// Esto solo decide COMO SE LEE:
//   - Cotizacion AUTORIZADA .... negro, normal. Es dinero real.
//   - Cotizacion sin autorizar .. gris claro. Es dinero probable, no confirmado.
//   - SIN cotizacion ........... NO se dice "sin monto": se dice "Sin cotizar · N dias", contando
//     desde el ALTA del expediente (no desde la entrada al paso). Ese es el dinero olvidado.
// Los umbrales de alerta son los mismos del semaforo de dias, para que la lectura sea coherente.
export function diasDesde(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

// Umbrales de "lleva demasiado sin cotizar" (dias desde el alta).
export const UMBRAL_SIN_COTIZAR = { amarillo: 5, rojo: 10 };

export function semaforoSinCotizar(dias) {
  const n = Number(dias) || 0;
  if (n >= UMBRAL_SIN_COTIZAR.rojo) return 'rojo';
  if (n >= UMBRAL_SIN_COTIZAR.amarillo) return 'amarillo';
  return 'verde';
}

// Devuelve como pintar el monto de un expediente. Un solo sitio para toda la app.
//   { estado: 'autorizado'|'probable'|'sin_cotizar'|'oculto', texto, color, dias?, semaforo? }
//
// EXPEDIENTES-RESUMEN (2026-09-17) — el estado 'oculto'
// El servidor deja de mandar el monto de los expedientes ajenos a quien no sea direccion (ver
// middlewares/permisosUsuario.js) y marca esas filas con `monto_oculto`. Aqui ese caso se pinta
// VACIO: ni "$0" (seria mentira), ni "oculto"/un candado (llamaria la atencion sobre algo que a
// esa persona no le toca y no puede abrir). Simplemente no hay nada en ese hueco.
export function lecturaMonto(exp, compacto) {
  if (exp && exp.monto_oculto) {
    return { estado: 'oculto', texto: '', color: 'transparent', titulo: undefined };
  }
  if (exp && exp.monto != null) {
    const autorizado = !!exp.monto_autorizado;
    return {
      estado: autorizado ? 'autorizado' : 'probable',
      texto: formatMoneda(exp.monto, compacto),
      // gris claro = cifra aun no confirmada; negro = confirmada
      color: autorizado ? '#1f2937' : '#a8b0ba',
      titulo: autorizado ? 'Cotizacion autorizada' : 'Cotizacion aun no autorizada (dinero probable)',
    };
  }
  const dias = diasDesde(exp && exp.creado_at);
  const sem = SEMAFORO[semaforoSinCotizar(dias)] || SEMAFORO.verde;
  // MOVIL: en una tarjeta de 360 px "Sin cotizar · 58 dias" no cabe junto al folio y se desborda
  // (FRENTE E del sprint EXPEDIENTES-RESUMEN). En compacto se abrevia a "Sin cotizar 58d"; el
  // texto completo sigue vivo en el tooltip, asi que no se pierde informacion.
  return {
    estado: 'sin_cotizar',
    texto: dias == null
      ? 'Sin cotizar'
      : (compacto ? `Sin cotizar ${dias}d` : `Sin cotizar · ${dias} ${dias === 1 ? 'dia' : 'dias'}`),
    color: sem.color,
    bg: sem.bg,
    borde: sem.borde,
    dias,
    semaforo: semaforoSinCotizar(dias),
    titulo: dias == null
      ? 'Este expediente todavia no tiene ninguna cotizacion capturada'
      : `Sin cotizar desde hace ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
  };
}
